import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { OrderChartModel } from 'src/app/models/charts/preliminaries/order-chart.model';
import { ItemFilterModel } from 'src/app/models/filters/item-filter.model';
import { PendingsOrdersImportModel } from 'src/app/models/imports/pendings-orders-import.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { OrderDataModel } from 'src/app/models/preliminaries/order-data.model';
import { OrderModel } from 'src/app/models/preliminaries/order.model';
import { OrderPrintModel } from 'src/app/models/print/preliminaries/order-print.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { OrderSelectionTableModel } from 'src/app/models/tables/preliminaries/order-selection-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})

export class OrderService {

  private readonly _httpClient = inject(HttpClient);

  attachFilesToOrder(orderId: number, files: FormData): Observable<Array<string>> {
    return this._httpClient.post<Array<string>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.AttachFilesToOrder}/${orderId}`, files);
  }

  bulkCloseOrdersAsync(): Observable<boolean> {
    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.BulkCloseOrdersAsync}`, null);
  }

  createOrder(orderModel: OrderModel): Observable<OrderModel> {
    return this._httpClient.post<OrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.CreateOrder}`,
      orderModel
    );
  }

  deleteFilesToOrder(orderId: number, file: string): Observable<Array<string>> {
    const itemFilterResponse = new ItemFilterModel("file", null, orderId, file);
    return this._httpClient.post<Array<string>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.DeleteFilesToOrder}`,
      itemFilterResponse
    );
  }

  deleteOrder(orderId: number): Observable<OrderModel> {
    return this._httpClient.delete<OrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.DeleteOrder}/${orderId}`);
  }

  downloadFileToOrder(orderId: number, file: string): Observable<HttpEvent<Blob>> {
    const itemFilterResponse = new ItemFilterModel("file", null, orderId, file);
    return this._httpClient.request(new HttpRequest('POST', `${environment.apiCervinoBackend}${enpointCervinoWeb.order.DownloadFileToOrder}`,
      itemFilterResponse, { reportProgress: true, responseType: 'blob' })
    );
  }

  downloadGuideImport(): Observable<HttpEvent<Blob>> {
    return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.order.DownloadGuideImport}`,
      null, { reportProgress: true, responseType: 'blob' }));
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.ExportGeneralConsultation}`, filters, {
      params, responseType: 'blob',
    });
  }

  importPendingsOrders(pendingsOrdersImport: Array<PendingsOrdersImportModel>): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.ImportPendingsOrdersAsync}`, pendingsOrdersImport);
  }

  getAllOrdersWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetAllOrdersWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getAllClientsForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetAllClientsForSelectAsync}`,);
  }

  getAllOrderTypesForSelect(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetAllOrderTypesForSelectAsync}`);
  }

  getAllAttachmentsWithPaginationAsync(orderId: number, page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('orderId', orderId).set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetAllAttachmentsWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getAllCostCentersForSelect(branchId: number): Observable<Array<any>> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetAllCostCenterForSelectAsync}`,
      { params: params }
    );
  }

  getAllOrdersForSelect(branchId: number, costCenterId: number): Observable<Array<any>> {
    const params = new HttpParams().set('branchId', branchId).set('costCenterId', costCenterId);
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetAllOrderForSelectAsync}`,
      { params: params }
    );
  }

  getAllOrdersConsultationWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetAllOrdersConsultationWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getAllSellersForSelect(branchId: number): Observable<Array<any>> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetAllSellersForSelectAsync}`,
      { params: params }
    );
  }

  getAllConcepsForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetAllConcepsForSelectAsync}`);
  }

  getClientByIdForRegister(clientId: number): Observable<any> {
    const params = new HttpParams().set('clientId', clientId);
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetClientByIdForRegisterAsync}`,
      { params: params }
    );
  }

  getCostCenterByIdForRegister(costCenterId: number): Observable<any> {
    const params = new HttpParams().set('costCenterId', costCenterId);
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetCostCenterByIdForRegisterAsync}`,
      { params: params }
    );
  }

  getColumnsForAttachmentsAsync(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetColumnsForAttachmentsAsync}`);
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetColumnsForGeneralConsultationAsync}`);
  }

  getColumnsForOrdersConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetColumnsForOrdersConsultationAsync}`);
  }

  getColumnsForOrderSelection(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetColumnsForOrderSelectionAsync}`);
  }

  getFilesToOrder(orderId: number): Observable<Array<string>> {
    return this._httpClient.get<Array<string>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetFilesToOrder}/${orderId}`);
  }

  getFirstOrderByKeysAsync(branchId: number): Observable<OrderModel> {
    const params = new HttpParams().set('branchId', branchId)
    return this._httpClient.get<OrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetFirstOrderByKeys}`,
      { params: params }
    );
  }

  getFirstOrderByQuotationIdAsync(quotationId: number): Observable<OrderModel> {
    const params = new HttpParams().set('quotationId', quotationId);
    return this._httpClient.get<OrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetFirstOrderByQuotationId}`,
      { params: params }
    );
  }

  getEmptyOrderPrint(): Observable<OrderPrintModel> {
    return this._httpClient.get<OrderPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetEmptyOrderPrint}`);
  }

  getIndicatorsForGeneralConsultation(filters: ColumnResponseModel[]): Observable<OrderChartModel> {
    return this._httpClient.post<OrderChartModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetIndicatorsForGeneralConsultationAsync}`,
      filters
    );
  }

  getLastOrderByKeysAsync(branchId: number): Observable<OrderModel> {
    const params = new HttpParams().set('branchId', branchId)
    return this._httpClient.get<OrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetLastOrderByKeys}`,
      { params: params }
    );
  }

  getNextOrderByKeysAsync(branchId: number, consecutive: number | null): Observable<OrderModel> {
    let params = new HttpParams().set('branchId', branchId)
    if (consecutive !== null)
      params = params.set('consecutive', consecutive.toString());
    return this._httpClient.get<OrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetNextOrderByKeys}`,
      { params: params }
    );
  }

  getOrderById(orderId: number): Observable<OrderModel> {
    return this._httpClient.get<OrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetOrderById}/${orderId}`);
  }

  getOrderByKeysAsync(branchId: number, consecutive: number | null): Observable<OrderModel> {
    let params = new HttpParams().set('branchId', branchId)
    if (consecutive !== null)
      params = params.set('consecutive', consecutive.toString());
    return this._httpClient.get<OrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetOrderByKeys}`,
      { params: params }
    );
  }

  getOrderByKeysByPrintAsync(branchId: number, consecutive: number, orderId: number): Observable<OrderPrintModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive).set('orderId', orderId);
    return this._httpClient.get<OrderPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetOrderByKeysByPrintAsync}`,
      { params }
    );
  }

  getOrdersForSelection(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[], branchId: number, orderId: number = 0, swService: boolean = false):
    Observable<PagedResponseModel<OrderSelectionTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('branchId', branchId)
      .set('orderId', orderId).set('swService', swService);
    return this._httpClient.post<PagedResponseModel<OrderSelectionTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetOrdersForSelectionAsync}`,
      filters, { params: params }
    );
  }

  getOrderConsecutiveById(orderId: number): Observable<number> {
    const params = new HttpParams().set('orderId', orderId);
    return this._httpClient.get<{ value: number }>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetOrderConsecutiveByIdAsync}`,
      { params: params }).pipe(map(response => response.value));
  }

  getOrderDataById(orderId: number): Observable<OrderDataModel> {
    const params = new HttpParams().set('orderId', orderId);
    return this._httpClient.get<OrderDataModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetOrderDataByIdAsync}`,
      { params: params });
  }

  getPermissionOrder(permission: number): Observable<boolean> {
    return this._httpClient.get<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetPermission}/${permission}`);
  }

  getPreviusOrderByKeysAsync(branchId: number, consecutive: number | null): Observable<OrderModel> {
    let params = new HttpParams().set('branchId', branchId);
    if (consecutive !== null)
      params = params.set('consecutive', consecutive.toString());
    return this._httpClient.get<OrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.GetPreviusOrderByKeys}`,
      { params: params }
    );
  }

  revertOrderState(orderId: number): Observable<OrderModel> {
    return this._httpClient.put<OrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.RevertOrderState}/${orderId}`,
      null
    );
  }

  setStateOrder(orderId: number): Observable<OrderModel> {
    return this._httpClient.put<OrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.SetStateOrder}/${orderId}`,
      null
    );
  }

  updateOrder(orderContract: OrderModel): Observable<OrderModel> {
    return this._httpClient.put<OrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.order.UpdateOrder}`,
      orderContract
    );
  }
}
